import { apiClient } from '@/app/api/client';
import { marked } from 'marked';
import { geoUtils } from '@/utils/geoUtils';

export interface BlogSpotConnection {
  id: string;
  settings: {
    blogId: string;
    clientId: string;
    clientSecret: string;
    refreshToken: string;
  };
}

export interface PublishingProgress {
  connectionId: string;
  platformName: string;
  status: 'pending' | 'in_progress' | 'success' | 'error';
  message?: string;
  externalUrl?: string;
}

export interface BlogSpotPost {
  title: string;
  content: string;
  labels?: string[];
  summary?: string;
  authorSettings?: {
    jobTitle?: string;
    bio?: string;
    socialLinks?: {
      linkedin?: string;
      twitter?: string;
    };
  };
}

class BlogSpotPublishingService {
  private async refreshAccessToken(connection: BlogSpotConnection): Promise<string> {
    const { clientId, clientSecret, refreshToken } = connection.settings;

    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: clientId,
        client_secret: clientSecret,
        refresh_token: refreshToken,
        grant_type: 'refresh_token'
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to refresh token: ${errorData.error_description || response.statusText}`);
    }

    const tokenData = await response.json();
    return tokenData.access_token;
  }

  private convertMarkdownToHtml(markdown: string): string {
    if (!markdown) return '';

    // Configure marked for BlogSpot-friendly output
    marked.setOptions({
      breaks: false,       // Don't convert single \n to <br> (use double \n for paragraphs)
      gfm: true,          // GitHub Flavored Markdown
    });

    let html = marked.parse(markdown) as string;

    // Inject inline styles into table elements for external platform compatibility
    // (Blogspot/WordPress themes don't provide table border styles by default)
    html = this.styleTablesForExternalPlatform(html);

    return html;
  }

  /**
   * Inject inline styles into table HTML elements so tables render with
   * borders, padding, and header styling on external platforms.
   */
  private styleTablesForExternalPlatform(html: string): string {
    return html
      .replace(/<table>/gi,
        '<table style="border-collapse: collapse; width: 100%; margin: 1.5em 0; font-size: 0.95em;">')
      .replace(/<th>/gi,
        '<th style="border: 1px solid #d1d5db; padding: 10px 14px; background-color: #f3f4f6; font-weight: 600; text-align: left;">')
      .replace(/<th /gi,
        '<th style="border: 1px solid #d1d5db; padding: 10px 14px; background-color: #f3f4f6; font-weight: 600; text-align: left;" ')
      .replace(/<td>/gi,
        '<td style="border: 1px solid #d1d5db; padding: 10px 14px;">')
      .replace(/<td /gi,
        '<td style="border: 1px solid #d1d5db; padding: 10px 14px;" ');
  }

  private getPublicDomain(): string {
    // Get the public domain dynamically based on current environment
    if (typeof window === 'undefined') {
      // Fallback for server-side rendering
      return 'https://www.content-growth.com';
    }

    const wwwUrl = new URL(window.location.origin);
    wwwUrl.hostname = wwwUrl.hostname.replace(/^(app|api|[^.]+)\./, 'www.');
    return wwwUrl.origin;
  }

  private transformRelativeLinks(html: string, baseUrl: string): string {
    return html.replace(/(href=["'])\/(?!\/)([^"']*)(["'])/gi, `$1${baseUrl}/$2$3`);
  }

  private buildBlogSpotContent(htmlContent: string, title: string, summary?: string, authorSettings?: BlogSpotPost['authorSettings']): string {
    const publicDomain = this.getPublicDomain();
    htmlContent = this.transformRelativeLinks(htmlContent, publicDomain);

    let finalContent = '';

    // Add JSON-LD Schema (Invisible)
    const schema = geoUtils.generateArticleSchema({
      title,
      summary: summary || '',
      authorName: 'Content Growth User', // We might want to pass this in significantly later, or rely on profile
      authorJobTitle: authorSettings?.jobTitle,
      authorBio: authorSettings?.bio,
      authorSocialLinks: authorSettings?.socialLinks ? Object.values(authorSettings.socialLinks).filter(Boolean) as string[] : undefined,
      canonicalUrl: '', // BlogSpot generates its own URL, hard to know beforehand. omit for now.
    });
    finalContent += schema;

    // Add AI Summary if available
    if (summary) {
      finalContent += geoUtils.generateVisibleSummary(this.escapeHtml(summary));
    }

    // Add main article content
    finalContent += htmlContent;

    // Get public domain for links
    // Get public domain for links (already retrieved at start)
    const logoUrl = `${publicDomain}/logo.svg`;

    // Add Content Growth footer with logo
    finalContent += `
<div style="margin-top: 32px; padding-top: 24px; border-top: 2px solid #e5e7eb;">
  <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
    <img src="${logoUrl}" alt="Content Growth Logo" style="height: 24px; width: auto;" />
    <a href="${publicDomain}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: none; font-weight: 600; font-size: 14px;">
      Content Growth
    </a>
  </div>
  <p style="margin: 0; font-size: 13px; color: #6b7280; line-height: 1.6;">
    <em>
      This article is created and managed by
      <a href="${publicDomain}" target="_blank" rel="noopener noreferrer" style="color: #2563eb; text-decoration: none; font-weight: 500;">
        Content Growth
      </a>
      — Your AI-powered content creation and distribution platform.
    </em>
  </p>
</div>
`;

    return finalContent;
  }

  private escapeHtml(text: string): string {
    const map: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, (char) => map[char]);
  }

  async publishToBlogSpot(
    connection: BlogSpotConnection,
    post: BlogSpotPost,
    onProgress: (progress: PublishingProgress) => void
  ): Promise<string> {
    const { blogId } = connection.settings;

    try {
      onProgress({
        connectionId: connection.id,
        platformName: 'BlogSpot',
        status: 'in_progress',
        message: 'Refreshing access token...'
      });

      const accessToken = await this.refreshAccessToken(connection);

      onProgress({
        connectionId: connection.id,
        platformName: 'BlogSpot',
        status: 'in_progress',
        message: 'Publishing to BlogSpot...'
      });

      const htmlContent = this.convertMarkdownToHtml(post.content);
      const finalContent = this.buildBlogSpotContent(htmlContent, post.title, post.summary, post.authorSettings);

      const response = await fetch(`https://www.googleapis.com/blogger/v3/blogs/${blogId}/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: post.title,
          content: finalContent,
          labels: post.labels || []
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`BlogSpot API error: ${errorData.error?.message || response.statusText}`);
      }

      const result = await response.json();
      const externalUrl = result.url;

      onProgress({
        connectionId: connection.id,
        platformName: 'BlogSpot',
        status: 'success',
        message: 'Successfully published to BlogSpot',
        externalUrl
      });

      return result.id;
    } catch (error) {
      onProgress({
        connectionId: connection.id,
        platformName: 'BlogSpot',
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      throw error;
    }
  }

  async updateBlogSpotPost(
    connection: BlogSpotConnection,
    postId: string,
    post: BlogSpotPost,
    onProgress: (progress: PublishingProgress) => void
  ): Promise<void> {
    const { blogId } = connection.settings;

    try {
      onProgress({
        connectionId: connection.id,
        platformName: 'BlogSpot',
        status: 'in_progress',
        message: 'Updating BlogSpot post...'
      });

      const accessToken = await this.refreshAccessToken(connection);
      const htmlContent = this.convertMarkdownToHtml(post.content);
      const finalContent = this.buildBlogSpotContent(htmlContent, post.title, post.summary, post.authorSettings);

      const response = await fetch(`https://www.googleapis.com/blogger/v3/blogs/${blogId}/posts/${postId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: post.title,
          content: finalContent,
          labels: post.labels || []
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`BlogSpot API error: ${errorData.error?.message || response.statusText}`);
      }

      onProgress({
        connectionId: connection.id,
        platformName: 'BlogSpot',
        status: 'success',
        message: 'Successfully updated BlogSpot post'
      });
    } catch (error) {
      onProgress({
        connectionId: connection.id,
        platformName: 'BlogSpot',
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      throw error;
    }
  }

  async testConnection(connection: BlogSpotConnection): Promise<boolean> {
    try {
      const accessToken = await this.refreshAccessToken(connection);
      const { blogId } = connection.settings;

      const response = await fetch(`https://www.googleapis.com/blogger/v3/blogs/${blogId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        }
      });

      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

export const blogSpotPublishingService = new BlogSpotPublishingService();
